import { Component, ReactNode } from 'react';
import { Box, ImageX } from '@bridebook/ui';
import { OPTIMIZED_IMAGE_QUALITY } from '@bridebook/toolbox/src/buildImageUrl';
import Swipeable from '@bridebook/ui/src/components/bbcommon/swipeable/swipeable';
import { LightweightCarouselControls } from '@bridebook/web/app-shared/components/supplier/supplier-card/lightweight-carousel-controls';
import { ViewedEndScreen } from '@bridebook/web/app-shared/lib/supplier/types';
import { getSupplierCardSize } from '@bridebook/web/app-shared/lib/supplier/utils/get-supplier-card-size';
import CarouselPaging from './carousel-paging';
import FinalScreen from './final-screen';
import componentStyles from './lightweight-carousel.style';
import { MediaButton } from './media-button';
import { Image } from '@bridebook/aura/src/icons';

interface IProps {
  suppliedImages: string[];
  showOverlay?: boolean;
  showCounter?: boolean;
  showCarousel?: boolean;
  finalScreenText?: string;
  maxNumber?: number;
  isVenue: boolean;
  isSupplierProfile?: boolean;
  children?: ReactNode;
  onClick?: (index: number) => void;
  highRes?: boolean;
  onCarouselUsed?: (newIndex: number) => void;
  fullBleed?: boolean;
  eagerLoad?: boolean;
  showGradient?: boolean;
  onViewedEndScreen?: (opts: ViewedEndScreen) => void;
  isMobile: boolean;
  isTablet: boolean;
  controls?: boolean;
  // Changes the display property of carousel images to 'contents' which causes them to respect parent element's height bounds.
  limitImageOverflow?: boolean;
  isSSR?: boolean;
  noReinit?: boolean;
  supplierId?: string;
}

export interface ICarouselImage {
  src: string;
}

export const NEXT = 'NEXT';
export const PREV = 'PREV';

type TCarouselElement = ICarouselImage & {
  id: number;
  type: 'image' | 'overlay';
};

export interface ICarouselState {
  byId: Record<number, TCarouselElement>;
  slides: {
    originalSequence: number[];
    renderSequence: number[];
    count: number;
  };
  sliding: boolean;
  direction: typeof NEXT | typeof PREV | undefined;
  originalSequencePosition: number;
}

interface IState {
  suppliedImages: string[];
  carousel: ICarouselState;
  isFirstPictureLoaded: boolean;
}

const transformImages = (images: string[], maxNumber?: number): string[] => {
  if (maxNumber === undefined) return images;
  return maxNumber < images.length ? images.slice(0, maxNumber) : images;
};

const getOrder = (index: number, position: number, numItems: number) =>
  index - position < 0 ? numItems - Math.abs(index - position) : index - position;

class LightweightCarousel extends Component<IProps, IState> {
  timeoutByIds: Record<'prev' | 'next', ReturnType<typeof setTimeout> | null> = {
    prev: null,
    next: null,
  };

  constructor(props: IProps) {
    super(props);
    this.prepareCarousel();
  }

  componentDidUpdate(prevProps: IProps) {
    if (
      this.props.suppliedImages.length !== prevProps.suppliedImages.length &&
      !this.props.noReinit
    ) {
      this.prepareCarousel();
    }
  }

  clearTimeouts() {
    for (const [id, timeout] of Object.entries(this.timeoutByIds)) {
      if (timeout) clearTimeout(timeout);
      // @ts-expect-error
      this.timeoutByIds[id] = null;
    }
  }

  componentWillUnmount() {
    this.clearTimeouts();
  }

  prepareCarousel() {
    const { suppliedImages, showOverlay, maxNumber } = this.props;

    const centeredImages = transformImages(suppliedImages, maxNumber);
    const elements: TCarouselElement[] = centeredImages.map((image, index) => ({
      id: index,
      src: image,
      type: 'image',
    }));

    if (showOverlay) {
      elements.push({
        id: centeredImages.length,
        src: centeredImages[0],
        type: 'overlay',
      });
    }

    const byId = elements.reduce<Record<number, TCarouselElement>>((prev, element) => {
      prev[element.id] = element;
      return prev;
    }, {});

    // render last element as first for smooth and high perf transition left and right. the approach here uses transtion + flex order to achieve slide effect
    const elementIds = elements.map((element) => element.id);
    const slides = {
      originalSequence: [...elementIds],
      renderSequence: [...elementIds],
      count: elementIds.length,
    };
    const firstElement = slides.originalSequence[0];

    const lastElement = slides.renderSequence.pop();
    if (lastElement !== undefined) {
      slides.renderSequence.unshift(lastElement);
    }

    // magic, just double the items when there are just two items to have smooth transition to left and right (we need element on left and right)
    if (slides.renderSequence.length == 2) {
      slides.renderSequence = [...slides.renderSequence, ...slides.renderSequence];
      slides.originalSequence = [...slides.originalSequence, ...slides.originalSequence];
    }

    const newState = {
      suppliedImages: centeredImages,
      carousel: {
        sliding: false,
        direction: undefined,
        byId,
        slides,
        originalSequencePosition: 0,
        currentElement: firstElement,
      },
      isFirstPictureLoaded: false,
    };

    if (this.state) {
      this.setState(newState);
      return;
    } else {
      // eslint-disable-next-line react/no-direct-mutation-state
      this.state = newState;
    }
  }

  prev = () => {
    if (this.state.carousel.sliding) return;
    const { originalSequencePosition, slides } = this.state.carousel;
    const newIndex =
      originalSequencePosition === 0 ? slides.count - 1 : originalSequencePosition - 1;
    this.triggerCarouselUsed(newIndex);
    this.setState((prevState) => ({
      carousel: {
        ...prevState.carousel,
        originalSequencePosition: newIndex,
        sliding: true,
        direction: PREV,
      },
    }));

    this.timeoutByIds['prev'] = setTimeout(() => {
      this.timeoutByIds['prev'] = null;
      this.setState((prevState) => ({
        carousel: {
          ...prevState.carousel,
          sliding: false,
          direction: undefined,
        },
      }));
    }, 50);
  };

  triggerCarouselUsed(newIndex: number) {
    const { onCarouselUsed } = this.props;
    if (typeof onCarouselUsed === 'function') {
      onCarouselUsed(newIndex);
    }
  }

  next = () => {
    if (this.state.carousel.sliding) return;
    const { originalSequencePosition, slides } = this.state.carousel;
    const newIndex =
      originalSequencePosition === slides.count - 1 ? 0 : originalSequencePosition + 1;
    this.triggerCarouselUsed(newIndex);
    this.setState((prevState) => ({
      carousel: {
        ...prevState.carousel,
        originalSequencePosition: newIndex,
        sliding: true,
        direction: NEXT,
      },
    }));

    this.timeoutByIds['next'] = setTimeout(() => {
      this.timeoutByIds['next'] = null;
      this.setState((prevState) => ({
        carousel: {
          ...prevState.carousel,
          sliding: false,
          direction: undefined,
        },
      }));
    }, 50);
  };

  onClick = () => {
    const { onClick } = this.props;
    if (onClick) {
      onClick(this.state.carousel.originalSequencePosition);
    }
  };

  render() {
    const {
      showCarousel,
      showCounter,
      finalScreenText,
      children,
      isSupplierProfile,
      highRes,
      fullBleed,
      eagerLoad,
      showGradient,
      onViewedEndScreen,
      limitImageOverflow,
      isSSR,
      supplierId,
    } = this.props;

    const {
      carousel: { slides, originalSequencePosition, byId, sliding, direction },
    } = this.state;

    const [width, height] = getSupplierCardSize({
      isSupplierProfile,
      highRes,
      fullBleed,
    });

    const styles = componentStyles({ fullBleed, limitImageOverflow });
    const currentElement = byId[slides.originalSequence[originalSequencePosition]];
    const firstElement = byId[slides.originalSequence[0]];
    const setLoadingType = (elementId: number, order: number) => {
      const preloadImageCurrentPrevNext =
        eagerLoad && [0, 1, 2].some((preloaded) => preloaded === order);

      if (isSupplierProfile) {
        return preloadImageCurrentPrevNext && this.state.isFirstPictureLoaded
          ? 'eager'
          : elementId === firstElement.id
            ? 'eager'
            : 'lazy';
      }

      return preloadImageCurrentPrevNext ? 'eager' : 'auto';
    };

    const setFirstPictureLoaded = (elementId: number) => {
      if (elementId === firstElement.id) {
        this.setState((prevState) => ({
          ...prevState,
          isFirstPictureLoaded: true,
        }));
      }
    };

    const carousel = (
      <Box style={styles.imageWrap} name="image-wrap" onClick={this.onClick}>
        <Box style={styles.container} name="lightweight-carousel-container">
          <Box
            style={styles.slidesWrap({
              sliding,
              direction,
              count: slides.count,
            })}>
            {slides.renderSequence.map((slide, index) => {
              const element = byId[slide];
              const order = getOrder(index, originalSequencePosition, slides.renderSequence.length);
              return (
                <div
                  key={`${element.id}-${index}`}
                  style={styles.loadCarouselStyle(order)}
                  data-name="carousel-div">
                  <ImageX
                    src={element.src}
                    style={styles.imageStyle}
                    w={width}
                    h={height}
                    loading={setLoadingType(element.id, order)}
                    onLoad={() => setFirstPictureLoaded(element.id)}
                    quality={OPTIMIZED_IMAGE_QUALITY}
                    imageStyle={{ height: '100%', width: '100%', objectFit: 'cover' }}
                    isSSR={isSSR}
                  />
                  {element.type === 'overlay' && (
                    <FinalScreen
                      isVisible={currentElement.id === element.id}
                      finalScreenText={finalScreenText}
                      imagesCount={slides.count}
                      firstImageUrl={element.src}
                      supplierId={supplierId}
                      onViewedEndScreen={onViewedEndScreen}
                    />
                  )}
                </div>
              );
            })}
          </Box>

          {showCarousel && slides.count > 1 && (
            <CarouselPaging imagesCount={slides.count} currentIndex={originalSequencePosition} />
          )}
          {showCounter && slides.count > 1 && (
            <Box style={styles.counter}>
              <MediaButton icon={Image}>
                {originalSequencePosition + 1}/{slides.count}
              </MediaButton>
            </Box>
          )}
        </Box>
        {showGradient && <Box style={styles.imageFilter(true)} name="supplier-box-img-filter" />}
        {children}
      </Box>
    );

    return (
      <LightweightCarouselControls
        controls={this.props.controls}
        isTablet={this.props.isTablet}
        isMobile={this.props.isMobile}
        onNext={this.next}
        onPrev={this.prev}>
        {showCarousel && slides.count > 1 ? (
          <Swipeable
            style={{ height: '100%' }}
            onSwipedLeft={this.next}
            onSwipedRight={this.prev}
            preventScrollOnSwipe>
            {carousel}
          </Swipeable>
        ) : (
          carousel
        )}
      </LightweightCarouselControls>
    );
  }
}

export default LightweightCarousel;
